import {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  fetcher,
  useCourseData,
} from "@repo/edtech-api";
import {
  IS_WEBCOM,
  isEqual,
} from "@repo/edtech-shared";
import {
  PlayerType,
  usePlayerAllActions,
  usePlayersPause,
  usePlayerStore,
} from "@repo/edtech-video-player-utils";

import {
  Course,
  CURRENT_LESSON_INDEX_KEY,
  CURRENT_PUBLICATIONID_KEY,
  CURRENT_UNITID_KEY,
  ExtendedLesson,
  LessonType,
  PUBLICATION_ID,
  UNIT_ID,
  useMainContextActions,
  useMainContextValues,
} from "../model";
import { useAnalyticsContext } from "../model/providers/analytics-provider";
import { getConfig } from "../origins";
import { makeLessonNodeId } from "./lesson-library";
import {
  isLessonUnit,
  isValidLesson,
} from "./utils";

const formatValue = (val: unknown): string =>
  typeof val === "string" ?
    val :
    String(val);

type Props = {
  localState: number,
  changeLocalStateAction: (state: string) => void,
};

// todo refactor or make useSyncSearchParam more reusable
// TODO move to edtech-shared-lib
export const useSyncSearchParams = ({
  localState,
  changeLocalStateAction,
}: Props) => {

  /** Синхронизировать фильтры из URL в состояние, если URL изменился */
  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const stateFromUrl = params.get(CURRENT_LESSON_INDEX_KEY);

    if (!stateFromUrl || isEqual(stateFromUrl, String(localState))) return;

    changeLocalStateAction(stateFromUrl);

  }, []);

  /** Синхронизировать фильтры из состояния в URL при их изменении */
  useEffect(() => {
    const syncWithUrl = (searchValue: string) => {
      const url = new URL(window.location.href);
      const currentParams = new URLSearchParams(url.search);

      currentParams.set(CURRENT_LESSON_INDEX_KEY, formatValue(searchValue));

      if (!IS_WEBCOM) {
        currentParams.set(CURRENT_PUBLICATIONID_KEY, currentParams.get(CURRENT_PUBLICATIONID_KEY) ?? PUBLICATION_ID);
        currentParams.set(CURRENT_UNITID_KEY, currentParams.get(CURRENT_UNITID_KEY) ?? UNIT_ID);
      }

      const newUrl = `${url.protocol}//${url.host}${url.pathname}?${currentParams.toString()}`;
      if (url.toString() !== newUrl) {
        window.history.replaceState(
          {
            path: newUrl,
          }, "", newUrl,
        );
      }
    };

    syncWithUrl(String(localState));
  }, [ localState ]);
};

type RequestType = Course | undefined | { available: false };

export type LessonsLibrary = Record<string, ExtendedLesson>;
type UnitsChildren = Record<string, string[]>;
type UnitQueueItem = {
  nodeId: string,
  sourceUid: string,
  parentNodeId: string | null,
  parentSourceUid: string | null,
  parentNodeIds: string[] | null,
  parentAvailable: boolean,
};

export const useMainData = ({
  publicationId,
  unitId,
  link,
}: {
  publicationId: string,
  unitId: string,
  link?: string,
}) => {
  const { courseViewUrl } = getConfig();
  const {
    data: mainCourse,
    isLoading: isMainCourseLoading,
    isValidating,
  } = useCourseData<Course>(
    publicationId, unitId, link, courseViewUrl,
  );

  const [ library, setLibrary ] = useState<LessonsLibrary>({});
  const [ unitsChildren, setUnitsChildren ] = useState<Record<string, string[]>>({});
  const [ onlyLessonsList, setOnlyLessonsList ] = useState<string[]>([]);
  const [ rootNodeIds, setRootNodeIds ] = useState<string[]>([]);
  const [ passedLessonsCount, setPassedLessonsCount ] = useState(0);
  const [ progressLessonsCount, setProgressLessonsCount ] = useState(0);
  const [ progressCompletedLessonsCount, setProgressCompletedLessonsCount ] = useState(0);
  const [ isAllRootElementsFinished, setIsAllRootElementsFinished ] = useState(false);

  const [ isAllSettled, setIsAllSettled ] = useState(false);
  const [ isFirstLoading, setIsFirstLoading ] = useState(true);

  useEffect(() => {
    if (!mainCourse || isValidating) return;

    setIsAllSettled(false);

    let localRootNodeIds: string[] = [];
    let localLibrary: LessonsLibrary = {};
    let localUnitsChildren: UnitsChildren = {};
    let requestsStack: UnitQueueItem[] = [];
    const addToUnitsChildren = (parentNodeId: string, childrenNodeIds: string[]) => {
      localUnitsChildren = {
        ...localUnitsChildren,
        [ parentNodeId ]: childrenNodeIds,
      };
    };

    const addToLibrary = (nodeId: string, lesson: ExtendedLesson) => {
      localLibrary = {
        ...localLibrary,
        [ nodeId ]: lesson,
      };
    };

    const addToRequestStack = (item: UnitQueueItem) => {
      requestsStack.push(item);
    };

    const getPassedLessons = (nodeIds: string[]) => {
      return nodeIds.filter((nodeId) => localLibrary[ nodeId ]?.isFinished === true).length;
    };

    const getProgressStats = (nodeIds: string[]) => {
      const progressLessonsNodeIds = nodeIds.filter((nodeId) => !localLibrary[ nodeId ]?.notALesson);

      return {
        progressLessonsCount: progressLessonsNodeIds.length,
        progressCompletedLessonsCount: getPassedLessons(progressLessonsNodeIds),
      };
    };

    mainCourse?.elements?.forEach((lesson, index) => {
      if (!isValidLesson(lesson)) return;

      const {
        elementSource,
        uid,
      } = lesson;
      const nodeId = makeLessonNodeId(
        null, uid, index,
      );
      const rootLesson = {
        ...lesson,
        nodeId,
        parentNodeIds: null,
        unitParentId: mainCourse.uid,
        isParentAvailable: true,
      } as ExtendedLesson;

      localRootNodeIds.push(nodeId);
      addToLibrary(nodeId, rootLesson);
      if (!isLessonUnit(elementSource)) return;

      addToRequestStack({
        nodeId,
        parentAvailable: true,
        parentNodeId: null,
        parentNodeIds: null,
        parentSourceUid: mainCourse.uid,
        sourceUid: uid,
      });
    });

    const fetchCurrentRequestStack = async () => {
      const currentBatch = [ ...requestsStack ];
      requestsStack = [];

      const units = await Promise.allSettled(currentBatch.map((item) => fetcher<RequestType>({
        url: courseViewUrl,
        params: {
          publicationid: publicationId,
          unitid: item.sourceUid,
        },
      })));

      units.forEach((promise, index) => {
        const item = currentBatch[ index ]!;

        if (promise.status !== "fulfilled" || !promise.value) {
          return;
        }

        if (promise.value.available === false) {
          return;
        }

        const {
          uid,
          elements,
          description,
        } = promise.value;

        const parentLesson = localLibrary[ item.nodeId ];
        const parentAvailable = item.parentAvailable &&
          parentLesson?.hideCard !== true &&
          parentLesson?.isParentAvailable !== false;
        const parentNodeIds = item.parentNodeIds ?
          [ ...item.parentNodeIds, item.nodeId ] :
          [ item.nodeId ];
        const validChildrenNodeIds: string[] = [];
        const childrenLength = elements?.length ?? 0;

        elements?.forEach((childLesson, childIndex) => {
          if (!isValidLesson(childLesson)) return;

          const childNodeId = makeLessonNodeId(
            item.nodeId, childLesson.uid, childIndex,
          );
          const childLessonNode = {
            ...childLesson,
            nodeId: childNodeId,
            parentNodeIds,
            unitDescription: childIndex === 0 ?
              description :
              undefined,
            isLastLessonInUnit: childIndex === childrenLength - 1,
            unitParentId: uid,
            isParentAvailable: parentAvailable,
          } as ExtendedLesson;

          validChildrenNodeIds.push(childNodeId);
          addToLibrary(childNodeId, childLessonNode);
          if (isLessonUnit(childLesson.elementSource)) {
            addToRequestStack({
              nodeId: childNodeId,
              parentAvailable,
              parentNodeId: item.nodeId,
              parentNodeIds,
              parentSourceUid: uid,
              sourceUid: childLesson.uid,
            });
          }
        });

        addToUnitsChildren(item.nodeId, validChildrenNodeIds);
      });
    };

    const processUnits = async () => {
      while (requestsStack.length > 0) {
        await fetchCurrentRequestStack();
      }

      const prunedUnitsChildren: UnitsChildren = {};

      const pruneNodeTree = (nodeId: string): boolean => {
        const lesson = localLibrary[ nodeId ];

        if (!lesson) return false;

        const children = localUnitsChildren[ nodeId ] ?? [];
        const keptChildren = children.filter((childNodeId) => pruneNodeTree(childNodeId));

        if (keptChildren.length > 0) {
          prunedUnitsChildren[ nodeId ] = keptChildren;
        }

        if (isLessonUnit(lesson.elementSource) && keptChildren.length === 0) {
          delete localLibrary[ nodeId ];
          return false;
        }

        return true;
      };

      const keptRootNodeIds = localRootNodeIds.filter((nodeId) => pruneNodeTree(nodeId));
      localUnitsChildren = prunedUnitsChildren;
      localRootNodeIds = keptRootNodeIds;

      const isAllRootElementsFinishedNow = localRootNodeIds
        .every((nodeId) => localLibrary[ nodeId ]?.isFinished === true);

      const updOnlyLessonsList: string[] = [];
      const getChildLessons = (nodeId: string): string[] => {
        const children = localUnitsChildren[ nodeId ];

        if (!children || !Array.isArray(children)) {
          return [ nodeId ];
        }

        const result: string[] = [];
        for (const childNodeId of children) {
          result.push(...getChildLessons(childNodeId));
        }

        return result;
      };

      for (const nodeId of localRootNodeIds) {
        updOnlyLessonsList.push(...getChildLessons(nodeId));
      }

      setOnlyLessonsList(updOnlyLessonsList);
      setRootNodeIds(localRootNodeIds);
      setIsAllRootElementsFinished(isAllRootElementsFinishedNow);
      setPassedLessonsCount(getPassedLessons(updOnlyLessonsList));
      const {
        progressLessonsCount: updProgressLessonsCount,
        progressCompletedLessonsCount: updProgressCompletedLessonsCount,
      } = getProgressStats(updOnlyLessonsList);
      setProgressLessonsCount(updProgressLessonsCount);
      setProgressCompletedLessonsCount(updProgressCompletedLessonsCount);
      setLibrary(localLibrary);
      setUnitsChildren(localUnitsChildren);

      setIsAllSettled(true);
      setIsFirstLoading(false);
    };

    processUnits();
  }, [
    courseViewUrl,
    isValidating,
    mainCourse,
    publicationId,
  ]);

  return {
    data: mainCourse,
    isLoading: isMainCourseLoading || !isAllSettled,
    isFirstLoading,
    library,
    setLibrary,
    onlyLessonsList,
    rootNodeIds,
    unitsChildren,
    passedLessonsCount,
    progressLessonsCount,
    progressCompletedLessonsCount,
    isAllRootElementsFinished,
  };
};

export const useHeightIndentationSync = (baseDynamicIndentation = 24) => {
  // aside размонтируется на мобильной ширине и монтируется заново при расширении,
  // поэтому храним его в state: эффект должен переподписаться на новый DOM-узел,
  // иначе отступ считается по отсоединенному элементу (его bottom = 0)
  const [ asideEl, asideRef ] = useState<HTMLElement | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const [ dynamicIndentation, setDynamicIndentation ] = useState(baseDynamicIndentation);

  useEffect(() => {
    if (!asideEl) return;

    const updateOffset = () => {
      const mainEl = mainRef.current;
      const firstChild = asideEl.firstElementChild;

      if (!mainEl || !firstChild || asideEl.children.length < 2) {
        setDynamicIndentation(baseDynamicIndentation);
        return;
      }

      const header = mainEl.parentElement?.firstElementChild;
      const targetEl = header?.nextElementSibling ?? mainEl;

      const firstChildBottom = firstChild.getBoundingClientRect().bottom;
      const targetTop = targetEl.getBoundingClientRect().top;

      setDynamicIndentation(Math.max(0, targetTop - firstChildBottom));
    };

    const observer = new ResizeObserver(updateOffset);
    observer.observe(asideEl);

    const section = asideEl.parentElement;

    if (section) {
      observer.observe(section);
    }

    updateOffset();

    return () => observer.disconnect();
  }, [ asideEl, baseDynamicIndentation ]);

  return [
    asideRef,
    mainRef,
    dynamicIndentation,
  ] as const;
};

export const usePlayersInteractions = () => {
  const {
    lessonNodeIds,
    currentLessonIndex,
    currentLesson,
  } = useMainContextValues();
  const { setLessonAction } = useMainContextActions();

  const {
    enterMini,
    exitMini,
    setAnalyticsTracker: setAudioAnalytics,
  } = usePlayerAllActions(PlayerType.Audio);

  const { setAnalyticsTracker: setVideoAnalytics } = usePlayerAllActions(PlayerType.Video);

  const videoPlaying = usePlayerStore(PlayerType.Video, (s) => s.playing);
  const audioId = usePlayerStore(PlayerType.Audio, (s) => s.id);

  const { onFinishLesson } = useAnalyticsContext();

  const audioMiniReentryBlockedRef = useRef(false);

  usePlayersPause();

  useEffect(() => {
    audioMiniReentryBlockedRef.current = false;
  }, [ audioId ]);

  useEffect(() => {
    if (currentLesson?.materialType === LessonType.Longread2) {
      setVideoAnalytics(() => null);
      setAudioAnalytics(() => null);
    } else {
      setVideoAnalytics(onFinishLesson);
      setAudioAnalytics(onFinishLesson);
    }
  }, [
    onFinishLesson,
    setVideoAnalytics,
    setAudioAnalytics,
    currentLesson?.materialType,
  ]);

  useEffect(() => {
    const videoStore = window.videoPlayerStore;
    const audioStore = window.audioPlayerStore;
    if (!videoStore || !audioStore || !lessonNodeIds?.length) return;

    const extractUid = (raw?: string | null) => {
      if (!raw) return null;
      const i = raw.indexOf("::");
      return i === -1 ?
        raw :
        raw.slice(0, i);
    };

    const videoUid = extractUid(videoStore.get().id);
    const audioUid = extractUid(audioStore.get().id);

    const idxVideo = videoUid ?
      lessonNodeIds.indexOf(videoUid) + 1 :
      -1;
    const idxAudio = audioUid ?
      lessonNodeIds.indexOf(audioUid) + 1 :
      -1;

    if (idxVideo !== -1) {
      videoStore.set({
        initialLessonIndex: idxVideo,
      });
    }
    if (idxAudio !== -1) {
      audioStore.set({
        initialLessonIndex: idxAudio,
      });
    }

    if (idxAudio !== -1) {
      const isMiniNow = audioStore.get().isMini;
      const canAutoEnter = idxAudio !== currentLessonIndex + 1 && !isMiniNow;

      if (canAutoEnter) enterMini();
      if (idxAudio === currentLessonIndex + 1) exitMini();
    }

    const disposeVideoBack = videoStore.miniActions.setMiniBackButtonHandler(() => {
      const ini = videoStore.get().initialLessonIndex;
      if (ini !== currentLessonIndex + 1) setLessonAction(ini);
    });

    const disposeAudioExit = audioStore.miniActions.setMiniBackButtonHandler(() => {
      const ini = audioStore.get().initialLessonIndex;
      if (ini !== currentLessonIndex + 1) setLessonAction(ini);
    });

    return () => {
      disposeVideoBack?.();
      disposeAudioExit?.();
    };
  }, [
    lessonNodeIds,
    currentLessonIndex,
    setLessonAction,
    videoPlaying,
    enterMini,
    audioId,
  ]);

  useEffect(() => {
    const audioStore = window.audioPlayerStore;
    if (!audioStore) return;

    const el = audioStore.playerActions.getPlayerElement?.() as HTMLMediaElement | null;
    const ensureExit = () => {
      if (audioStore.get().isMini) exitMini();
    };

    if (!el) {
      ensureExit();
      return;
    }

    const onEnded = () => ensureExit();

    const onEmptied = () => {
      if (!audioStore.get().source) ensureExit();
    };

    el.addEventListener("ended", onEnded);
    el.addEventListener("emptied", onEmptied);

    if (el.ended) ensureExit();

    return () => {
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("emptied", onEmptied);
    };
  }, [ exitMini, audioId ]);
};
